import React, { useState } from "react";
import "./auth.css";
import { useNavigate, useParams } from "react-router-dom";
import Logo from "../../../assets/LogoNew2.png";
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  InputAdornment,
  IconButton,
  CircularProgress,
  Divider,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import LockIcon from "@mui/icons-material/Lock";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import LockResetIcon from "@mui/icons-material/LockReset";
import { useFormik } from "formik";
import { ToastContainer, toast } from "react-toastify";
import { resetPasswordSchema } from "./validationSchema";
import axios from "axios";
import { BASE_URL } from "../../../config/config";
import { useTheme } from "../../../context/ThemeContext";
import { backgroundPattern } from "../../../config/themes";
import ThemePicker from "../../../components/ThemePicker";

const ResetPassword = () => {
  const navigate = useNavigate();
  const { token } = useParams();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const { theme } = useTheme();

  const formik = useFormik({
    initialValues: { password: "", confirmPassword: "" },
    validationSchema: resetPasswordSchema,
    onSubmit: async (values, action) => {
      setLoading(true);
      try {
        const response = await axios.post(
          `${BASE_URL}/reset-password/${token}`,
          {
            password: values.password,
          }
        );
        if (response.data.success) {
          toast.success("Password reset successfully!", {
            position: "top-center",
            autoClose: 2000,
          });
          setTimeout(
            () => navigate("/login", { state: { type: "Landlord" } }),
            2200
          );
        } else {
          toast.error(response.data.message || "Invalid or expired link", {
            position: "top-center",
          });
        }
      } catch (err) {
        toast.error("Something went wrong", { position: "top-center" });
      } finally {
        setLoading(false);
        action.resetForm();
      }
    },
  });

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: theme.gradient,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        overflow: "hidden",
        py: 4,
        transition: "background 0.5s ease",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          opacity: 0.1,
          backgroundImage: backgroundPattern,
        }}
      />

      <ThemePicker position="top-right" light={true} />

      <Container maxWidth="sm" sx={{ position: "relative", zIndex: 1 }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, sm: 5 },
            borderRadius: 4,
            bgcolor: "white",
            boxShadow: "0 25px 80px rgba(0,0,0,0.4)",
            position: "relative",
          }}
        >
          <Box sx={{ textAlign: "center", mb: 3 }}>
            {/* <Box component="img" src={Logo} alt="OTS Logo" sx={{ height: 60, mb: 2, cursor: "pointer" }} onClick={() => navigate("/")} /> */}
            <Box
              sx={{
                height: 100,
                width: 100,
                mb: 1,
                display: "inline-block",
                backgroundColor: theme.primary,

                maskImage: `url(${Logo})`,
                WebkitMaskImage: `url(${Logo})`,
                maskRepeat: "no-repeat",
                WebkitMaskRepeat: "no-repeat",

                maskSize: "120%",
                WebkitMaskSize: "120%",

                maskPosition: "center",
                WebkitMaskPosition: "center",
              }}
              onClick={() => navigate("/")}
            />

            <Box
              sx={{
                width: 70,
                height: 70,
                borderRadius: "50%",
                bgcolor: theme.lightBg,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mx: "auto",
                mb: 2,
                transition: "all 0.3s ease",
              }}
            >
              <LockResetIcon
                sx={{
                  fontSize: 35,
                  color: theme.primary,
                  transition: "color 0.3s ease",
                }}
              />
            </Box>

            <Typography
              variant="h5"
              sx={{ fontWeight: 700, color: "#1a1a2e", mb: 1 }}
            >
              Reset Password
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "#666", maxWidth: 300, mx: "auto" }}
            >
              Create a new password for your account
            </Typography>
          </Box>

          {loading && (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                bgcolor: "rgba(255,255,255,0.8)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 10,
                borderRadius: 4,
              }}
            >
              <CircularProgress sx={{ color: theme.primary }} />
            </Box>
          )}

          <form onSubmit={formik.handleSubmit}>
            <TextField
              fullWidth
              label="New Password"
              name="password"
              type={showPassword ? "text" : "password"}
              value={formik.values.password}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.password && Boolean(formik.errors.password)}
              helperText={formik.touched.password && formik.errors.password}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon sx={{ color: "#999" }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                mb: 2,
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  "&:hover fieldset": { borderColor: theme.primary },
                  "&.Mui-focused fieldset": { borderColor: theme.primary },
                },
                "& .MuiInputLabel-root.Mui-focused": { color: theme.primary },
              }}
            />

            <TextField
              fullWidth
              label="Confirm Password"
              name="confirmPassword"
              type={showConfirm ? "text" : "password"}
              value={formik.values.confirmPassword}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.confirmPassword &&
                Boolean(formik.errors.confirmPassword)
              }
              helperText={
                formik.touched.confirmPassword && formik.errors.confirmPassword
              }
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon sx={{ color: "#999" }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowConfirm(!showConfirm)}
                      edge="end"
                    >
                      {showConfirm ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                mb: 2.5,
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  "&:hover fieldset": { borderColor: theme.primary },
                  "&.Mui-focused fieldset": { borderColor: theme.primary },
                },
                "& .MuiInputLabel-root.Mui-focused": { color: theme.primary },
              }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading}
              sx={{
                py: 1.5,
                background: `linear-gradient(135deg, ${theme.primary} 0%, ${theme.secondary} 100%)`,
                fontWeight: 600,
                fontSize: "1rem",
                borderRadius: 2,
                textTransform: "none",
                boxShadow: `0 4px 20px ${theme.primary}50`,
                "&:hover": {
                  background: `linear-gradient(135deg, ${theme.primaryHover} 0%, ${theme.primary} 100%)`,
                  boxShadow: `0 6px 25px ${theme.primary}70`,
                },
                "&:disabled": { bgcolor: "#ccc" },
              }}
            >
              Reset Password
            </Button>
          </form>

          <Divider sx={{ my: 3 }} />

          <Box sx={{ textAlign: "center" }}>
            <Button
              variant="text"
              startIcon={<ArrowBackIcon />}
              onClick={() =>
                navigate("/login", { state: { type: "Landlord" } })
              }
              sx={{
                color: "#666",
                textTransform: "none",
                fontWeight: 500,
                "&:hover": { color: theme.primary, bgcolor: "transparent" },
              }}
            >
              Back to Login
            </Button>
          </Box>

          <Box sx={{ textAlign: "center", mt: 1 }}>
            <Button
              variant="text"
              onClick={() => navigate("/")}
              sx={{
                color: "#999",
                textTransform: "none",
                "&:hover": { color: theme.primary, bgcolor: "transparent" },
              }}
            >
              Back to Home
            </Button>
          </Box>
        </Paper>

        <Typography
          variant="body2"
          sx={{ textAlign: "center", mt: 3, color: "rgba(255,255,255,0.6)" }}
        >
          Owner Tenant Solution - Pakistan's #1 Rental Platform
        </Typography>
      </Container>

      <ToastContainer />
    </Box>
  );
};

export default ResetPassword;
